import { useCallback, useEffect, useState, useMemo } from 'react'

import Skeleton from '@mui/material/Skeleton'
import { Input, MultiSelect, RadioGroup, RadioGroupItem } from '@shopflo/ui'
import { AlertTriangle } from 'react-feather'
import ExportReportOnce, { NewExportFormErrorState } from 'src/components/common/export-component/steps/two/once'
import ExportReportRecurring from 'src/components/common/export-component/steps/two/recurring'
import { ExportConfigStep1, ExportConfigStep2, ExportFrequency } from 'src/components/common/export-component/types'
import { EmailMultiSelect } from 'src/components/common/form-elements/EmailMultiSelect'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { useReports } from 'src/contexts/ReportContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { validateEmail } from 'src/lib/utils/helpers'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'

interface Step2ContentProps {
  config: ExportConfigStep2
  step1Config: ExportConfigStep1
  onChange: (config: ExportConfigStep2) => void
  onValidationChange: (isValid: boolean) => void
}

const initialFormErrorState: NewExportFormErrorState = {
  emails: [],
  email: {
    status: false,
    errorCode: ''
  },
  startDate: {
    status: false,
    errorCode: ''
  },
  endDate: {
    status: false,
    errorCode: ''
  },
  reportName: {
    status: false,
    errorCode: ''
  }
}

const Step2Content = ({ config, step1Config, onChange, onValidationChange }: Step2ContentProps) => {
  const { user } = useUser()

  const [formErrors, setFormErrors] = useState<NewExportFormErrorState>(initialFormErrorState)
  const [isReportNameTouched, setIsReportNameTouched] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [isStep1Valid, setIsStep1Valid] = useState(false)
  const [emailInputText, setEmailInputText] = useState('')
  const [emailInFocus, setEmailInFocus] = useState(false)

  const { emails, isValidating, emailError } = useReports()

  const isExportingTodaysReport = step1Config.exportType === 'today'

  useEffect(() => {
    const hasEmailErrors = formErrors.emails.some(error => error.status)
    const hasReportNameErrors = !config.reportName || formErrors.reportName.status
    const isStep1Invalid = config.exportFrequency === 'once' && !isStep1Valid
    const hasEmailError = formErrors?.email?.status

    if (isExportingTodaysReport) {
      onValidationChange(!hasEmailError)
      return
    }

    onValidationChange(!hasEmailErrors && !hasReportNameErrors && !isStep1Invalid)
  }, [config.reportName, formErrors, isStep1Valid, config.exportFrequency])

  const validateReportName = useCallback(
    (value: string) => {
      const isValid = value.trim().length > 0 && value.trim().length <= 60
      setFormErrors(prev => ({
        ...prev,
        reportName: {
          status: !isValid,
          errorCode: !isValid ? 'DC_FORM_001' : ''
        }
      }))
      onValidationChange(isValid)
      return isValid
    },
    [onValidationChange]
  )

  const validateEmailInput = useCallback((value: string) => {
    const isValid = validateEmail(value)
    setFormErrors(prev => ({
      ...prev,
      email: { status: !isValid, errorCode: !isValid ? 'DC_FORM_024' : '' }
    }))
  }, [])

  // Add this new function to check all validations
  const handleRecurringScheduleChange = (schedule: ExportConfigStep2['schedule']) => {
    onChange({
      ...config,
      schedule,
      // Clear dateRange when switching to recurring
      dateRange: {
        startDate: '',
        endDate: ''
      }
    })
  }

  useEffect(() => {
    // Set default values first
    const defaultReportName = config.reportName || 'Untitled Report'
    onChange({
      ...config,
      reportName: defaultReportName,
      email: user?.email,
      emails: user?.email ? [user.email] : []
    })

    // Validate with the default value
    validateReportName(defaultReportName)

    if (!emailInputText) {
      validateEmailInput(emailInputText)
    }
    if (config.exportFrequency === 'once' && isStep1Valid) {
      if (isExportingTodaysReport) {
        onValidationChange(!formErrors?.email?.status)
      } else {
        onValidationChange(true)
      }
    }
  }, [])

  return (
    <div className='flex flex-col'>
      {!isExportingTodaysReport && (
        <>
          <RadioGroup
            value={config.exportFrequency}
            onValueChange={(value: string) => {
              onChange({
                ...config,
                exportFrequency: value as ExportFrequency
              })
              validateReportName(config.reportName)
            }}
            className='flex flex-row mt-2 mb-2 gap-8'
          >
            <RadioGroupItem value='once' id='export-once' label='One-time export' />
            <RadioGroupItem value='recurring' id='export-recurring' label='Recurring export' />
          </RadioGroup>

          <div className='w-full h-px bg-zinc-800/10 my-2' />

          <div className='flex items-center justify-between mt-4 mb-1 h-5'>
            <label className='text-zinc-500 text-text-xs-medium' htmlFor='report-name'>
              Report Title
            </label>
            <span className='text-xs text-gray-500'>{config.reportName?.length || 0}/60</span>
          </div>
          <div className='flex flex-col gap-1'>
            <Input
              id='report-name'
              variant='outline'
              dimension='md'
              value={config.reportName}
              maxLength={60}
              placeholder='Title'
              onChange={(e: any) => {
                onChange({
                  ...config,
                  reportName: e.target.value
                })
                validateReportName(e.target.value)
              }}
              onBlur={e => {
                validateReportName(e.target.value)
                setIsReportNameTouched(true)
              }}
              error={isReportNameTouched && formErrors.reportName.status}
            />
            {isReportNameTouched && formErrors.reportName.status && (
              <p className='text-xs text-red-600'>
                {errorMappings?.[formErrors?.reportName?.errorCode as keyof typeof errorMappings]}
              </p>
            )}
          </div>

          <div className='flex flex-col mt-6'>
            {config.exportFrequency === 'once' && (
              <ExportReportOnce config={config} onChange={onChange} onValidationChange={setIsStep1Valid} />
            )}
            {config.exportFrequency === 'recurring' && (
              <ExportReportRecurring onChange={handleRecurringScheduleChange} schedule={config.schedule} />
            )}
          </div>
        </>
      )}

      {isExportingTodaysReport && (
        <>
          <h2 className='text-text-xs-medium mt-4 mb-1'>Send report to:</h2>
          <InputTextField
            textFieldId='export-email-input'
            inputPlaceholder={'Enter email address'}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            errorStatus={!emailInFocus && emailTouched && formErrors?.email?.status}
            errorMessage={errorMappings?.[formErrors?.email?.errorCode as keyof typeof errorMappings]}
            inputValue={emailInputText}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onChange({
                ...config,
                email: event.target.value
              })
              setEmailInputText(event.target.value)
              validateEmailInput(event.target.value)
            }}
            handleBlur={() => {
              setEmailTouched(true)
              validateEmailInput(emailInputText)
              setEmailInFocus(false)
            }}
            handleFocus={() => setEmailInFocus(true)}
          />

          <div className='flex items-center gap-2 bg-yellow-50 rounded-md p-2 border border-yellow-500'>
            <AlertTriangle size={20} className='text-yellow-600' />
            <p className='text-sm'>Today's report can only be sent to your email address</p>
          </div>
        </>
      )}

      {!isExportingTodaysReport && (
        <>
          {emailError ? (
            <>
              <h2 className='text-base font-semibold mt-4'>Send report to</h2>
              <div className='flex items-center gap-2 bg-yellow-50 rounded-md p-2 border border-yellow-500'>
                <AlertTriangle size={20} className='text-yellow-600' />
                <p className='text-sm'>
                  You don't have access to download reports. Ask your store admin for permission
                </p>
              </div>
            </>
          ) : (
            <div>
              <h2 className='text-text-xs-medium text-zinc-500 mb-1 mt-6'>Send to</h2>
              {isValidating && !emailError ? (
                <Skeleton variant='text' width={200} height={70} sx={{ marginTop: '-10px' }} />
              ) : (
                <MultiSelect
                  options={emails.map((email: string) => ({ value: email, label: email }))}
                  value={config.emails || (user?.email ? [user.email] : [])}
                  onValueChange={selectedEmails => {
                    onChange({
                      ...config,
                      emails: selectedEmails
                    })
                  }}
                  placeholder='Select emails'
                  size='md'
                  searchable
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Step2Content
